import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import {
  Moon, Sun, Laptop,
  Palette, Lock,
  ShieldCheck, CreditCard,
  Key, Percent, Settings2,
  ChevronRight, ExternalLink,
  Loader2, Smartphone, Users,
  Phone, Mail, MessageCircle, MapPin, FileText,
  Youtube, Instagram, Send as Telegram,
  Save, AlertCircle, Eye, EyeOff, Copy, Check,
  MessageSquare, Radio, Building2, Tag,
  RefreshCw, ShieldAlert, Sparkles, HelpCircle,
  IndianRupee, Globe
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useGetSettingsQuery, useUpdateSettingsMutation } from "@/redux/slices/settingApi";
import { toast } from "sonner";

type SettingsTab = "general" | "razorpay" | "sms" | "security" | "pricing" | "contact" | "legal";

export default function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { data: settingsData, isLoading: isFetching, refetch } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();

  const [activeTab, setActiveTab] = useState<SettingsTab>("general");
  const [showKeySecret, setShowKeySecret] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [showSmsSecret, setShowSmsSecret] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    appName: "Ayurdnyanam",
    appLogo: "",
    appTagline: "The Premier Medical & Ayurvedic Learning Platform",
    paymentGateway: "razorpay",
    paymentMode: "live",
    paymentApiKey: "",
    paymentApiSecret: "",
    paymentWebhookSecret: "",
    smsOtpEnabled: true,
    smsProvider: "fast2sms",
    smsApiKey: "",
    smsSenderId: "AYURDN",
    smsEntityId: "",
    smsTemplateId: "",
    platformCharge: 0,
    taxRate: 0,
    notesPrice: 0,
    videosPrice: 0,
    examsPrice: 0,
    chapterPrice: 0,
    maxAllowedDevices: 1,
    blockSimultaneousLogin: true,
    supportEmail: "",
    supportWhatsApp: "",
    supportPhone: "",
    socialYoutube: "",
    socialInstagram: "",
    socialTelegram: "",
    instituteAddress: "",
    gstin: "",
    termsAndConditions: "",
    paymentPolicy: "",
  });

  const resetFormToData = () => {
    if (settingsData?.data) {
      const d = settingsData.data;
      setFormData({
        appName: d.appName || "Ayurdnyanam",
        appLogo: d.appLogo || "",
        appTagline: d.appTagline || "The Premier Medical & Ayurvedic Learning Platform",
        paymentGateway: d.paymentGateway || "razorpay",
        paymentMode: d.paymentMode || "live",
        paymentApiKey: d.paymentApiKey || "",
        paymentApiSecret: d.paymentApiSecret || "",
        paymentWebhookSecret: d.paymentWebhookSecret || "",
        smsOtpEnabled: d.smsOtpEnabled ?? true,
        smsProvider: d.smsProvider || "fast2sms",
        smsApiKey: d.smsApiKey || "",
        smsSenderId: d.smsSenderId || "AYURDN",
        smsEntityId: d.smsEntityId || "",
        smsTemplateId: d.smsTemplateId || "",
        platformCharge: d.platformCharge ?? 0,
        taxRate: d.taxRate ?? 0,
        notesPrice: d.notesPrice ?? 0,
        videosPrice: d.videosPrice ?? 0,
        examsPrice: d.examsPrice ?? 0,
        chapterPrice: d.chapterPrice ?? 0,
        maxAllowedDevices: d.maxAllowedDevices ?? 1,
        blockSimultaneousLogin: d.blockSimultaneousLogin ?? true,
        supportEmail: d.supportEmail || "",
        supportWhatsApp: d.supportWhatsApp || "",
        supportPhone: d.supportPhone || "",
        socialYoutube: d.socialYoutube || "",
        socialInstagram: d.socialInstagram || "",
        socialTelegram: d.socialTelegram || "",
        instituteAddress: d.instituteAddress || "",
        gstin: d.gstin || "",
        termsAndConditions: d.termsAndConditions || "",
        paymentPolicy: d.paymentPolicy || "",
      });
    }
  };

  useEffect(() => {
    resetFormToData();
  }, [settingsData]);

  const handleChange = (key: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleCopy = (text: string, fieldId: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSaveAll = async () => {
    try {
      await updateSettings(formData).unwrap();
      toast.success("All settings updated successfully!");
    } catch (err) {
      const error = err as { data?: { message?: string } };
      toast.error(error.data?.message || "Failed to update settings");
    }
  };

  const handleSaveTheme = async (newTheme: string) => {
    setTheme(newTheme);
    try {
      await updateSettings({ theme: newTheme }).unwrap();
    } catch (err) {
      // Silent fail
    }
  };

  const themeOptions = [
    {
      id: "light",
      name: "Light",
      icon: Sun,
      iconColor: "text-amber-500",
      bgColor: "bg-white",
      borderColor: "border-slate-200"
    },
    {
      id: "dark",
      name: "Dark",
      icon: Moon,
      iconColor: "text-indigo-400",
      bgColor: "bg-slate-950",
      borderColor: "border-slate-800"
    },
    {
      id: "system",
      name: "System",
      icon: Laptop,
      iconColor: "text-emerald-500",
      bgColor: "bg-slate-100 dark:bg-slate-900",
      borderColor: "border-slate-300 dark:border-slate-700"
    }
  ];

  const tabs = [
    {
      id: "general" as SettingsTab,
      label: "General & Branding",
      icon: Building2,
      desc: "App name, logos, and taglines",
      badge: "Core"
    },
    {
      id: "razorpay" as SettingsTab,
      label: "Razorpay & Payments",
      icon: CreditCard,
      desc: "Live/Test keys & webhooks",
      badge: formData.paymentMode === "live" ? "Live" : "Test",
      badgeVariant: formData.paymentMode === "live" ? "default" : "secondary"
    },
    {
      id: "sms" as SettingsTab,
      label: "SMS & OTP Gateway",
      icon: MessageSquare,
      desc: "Fast2SMS, MSG91, DLT templates",
      badge: formData.smsOtpEnabled ? "Active" : "Off"
    },
    {
      id: "security" as SettingsTab,
      label: "Security & Devices",
      icon: ShieldCheck,
      desc: "Device limits & session control",
      badge: `${formData.maxAllowedDevices === 999 ? "∞" : formData.maxAllowedDevices} Dev`
    },
    {
      id: "pricing" as SettingsTab,
      label: "Pricing & Charges",
      icon: Percent,
      desc: "Platform fee, GST & base rates",
      badge: `${formData.platformCharge}% Fee`
    },
    {
      id: "contact" as SettingsTab,
      label: "Contact & Support",
      icon: Phone,
      desc: "Email, WhatsApp & Socials",
      badge: "Public"
    },
    {
      id: "legal" as SettingsTab,
      label: "Legal & Policies",
      icon: FileText,
      desc: "Terms, privacy & refund policy",
      badge: "Compliance"
    }
  ];

  if (isFetching) {
    return (
      <div className="flex h-[80vh] items-center justify-center flex-col gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-sm font-medium text-muted-foreground animate-pulse">Loading platform settings...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background/50 pb-24">
      {/* Header with Global Actions */}
      <div className="border-b bg-card/60 backdrop-blur-md sticky top-0 z-20 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                <Settings2 className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">Admin Settings & Configuration</h1>
                <p className="text-xs text-muted-foreground">Manage your payment gateway, OTP configs, branding and global limits.</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={resetFormToData}
              disabled={isUpdating}
              className="rounded-xl h-9 text-xs font-semibold"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Discard
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={isUpdating}
              className="rounded-xl h-9 px-5 text-xs font-bold shadow-md bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving Changes...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> Save All Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Top Banner: Quick Theme & Status Switcher */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Compact Application Theme Selector */}
          <Card className="border shadow-xs bg-card/80 backdrop-blur-md rounded-2xl overflow-hidden p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <Palette className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold leading-tight">Theme Mode</h3>
                  <p className="text-[11px] text-muted-foreground">Interface appearance</p>
                </div>
              </div>
              <Badge variant="outline" className="text-[10px] uppercase font-bold tracking-wider capitalize">
                {theme || "system"}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {themeOptions.map(t => {
                const isSelected = theme === t.id;
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleSaveTheme(t.id)}
                    className={cn(
                      "flex items-center justify-center gap-2 py-2 px-3 rounded-xl border text-xs font-bold transition-all",
                      isSelected
                        ? "border-primary bg-primary/10 text-primary shadow-xs ring-1 ring-primary/30"
                        : "border-border/60 hover:bg-muted/50 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Icon className={cn("h-3.5 w-3.5", t.iconColor)} />
                    <span>{t.name}</span>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Live Razorpay Status Pill */}
          <Card className="border shadow-xs bg-card/80 backdrop-blur-md rounded-2xl overflow-hidden p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", formData.paymentMode === "live" ? "bg-emerald-500/10 text-emerald-500" : "bg-amber-500/10 text-amber-500")}>
                  <CreditCard className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold leading-tight">Payment Mode</h3>
                  <p className="text-[11px] text-muted-foreground">{formData.paymentApiKey ? `Key ID: ${formData.paymentApiKey.slice(0, 10)}...` : "Gateway unconfigured"}</p>
                </div>
              </div>
              <Badge className={cn("text-[10px] uppercase font-bold tracking-wider", formData.paymentMode === "live" ? "bg-emerald-500 text-white" : "bg-amber-500 text-white")}>
                {formData.paymentMode === "live" ? "● Live Razorpay" : "○ Test Sandbox"}
              </Badge>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border/40 text-xs">
              <span className="text-muted-foreground">Mode Switch:</span>
              <div className="flex items-center gap-2 font-medium">
                <span className={cn("text-[11px]", formData.paymentMode === "test" ? "text-amber-500 font-bold" : "text-muted-foreground")}>Test</span>
                <Switch
                  checked={formData.paymentMode === "live"}
                  onCheckedChange={(checked) => handleChange("paymentMode", checked ? "live" : "test")}
                />
                <span className={cn("text-[11px]", formData.paymentMode === "live" ? "text-emerald-500 font-bold" : "text-muted-foreground")}>Live</span>
              </div>
            </div>
          </Card>

          {/* SMS OTP Gateway Pill */}
          <Card className="border shadow-xs bg-card/80 backdrop-blur-md rounded-2xl overflow-hidden p-5 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2.5">
                <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", formData.smsOtpEnabled ? "bg-blue-500/10 text-blue-500" : "bg-muted text-muted-foreground")}>
                  <MessageSquare className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-bold leading-tight">SMS & OTP Status</h3>
                  <p className="text-[11px] text-muted-foreground">Provider: {formData.smsProvider.toUpperCase()}</p>
                </div>
              </div>
              <Badge variant={formData.smsOtpEnabled ? "default" : "secondary"} className="text-[10px] uppercase font-bold tracking-wider">
                {formData.smsOtpEnabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-border/40 text-xs">
              <span className="text-muted-foreground">OTP Login/Verification:</span>
              <Switch
                checked={formData.smsOtpEnabled}
                onCheckedChange={(v) => handleChange("smsOtpEnabled", v)}
              />
            </div>
          </Card>
        </div>

        {/* --- MAIN 2-COLUMN SETTINGS SYSTEM --- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* LEFT COLUMN: Category Navigation Sidebar */}
          <div className="lg:col-span-4 space-y-2 sticky top-24">
            <Card className="border shadow-sm bg-card/70 backdrop-blur-md rounded-2xl overflow-hidden p-2">
              <div className="p-3 pb-2">
                <p className="text-[11px] font-black uppercase tracking-wider text-muted-foreground">Settings Categories</p>
              </div>
              <nav className="space-y-1">
                {tabs.map((t) => {
                  const Icon = t.icon;
                  const isActive = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setActiveTab(t.id)}
                      className={cn(
                        "w-full flex items-center justify-between p-3 rounded-xl text-left transition-all duration-200 group",
                        isActive
                          ? "bg-primary text-primary-foreground shadow-md font-semibold"
                          : "hover:bg-muted/60 text-foreground/80 hover:text-foreground font-medium"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "h-9 w-9 rounded-xl flex items-center justify-center transition-all",
                            isActive
                              ? "bg-white/20 text-white"
                              : "bg-muted group-hover:bg-primary/10 text-muted-foreground group-hover:text-primary"
                          )}
                        >
                          <Icon className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm leading-tight font-bold">{t.label}</p>
                          <p
                            className={cn(
                              "text-[11px] line-clamp-1",
                              isActive ? "text-primary-foreground/80" : "text-muted-foreground"
                            )}
                          >
                            {t.desc}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {t.badge && (
                          <span
                            className={cn(
                              "text-[10px] px-2 py-0.5 rounded-full font-bold uppercase",
                              isActive
                                ? "bg-white/20 text-white"
                                : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                            )}
                          >
                            {t.badge}
                          </span>
                        )}
                        <ChevronRight
                          className={cn(
                            "h-4 w-4 transition-transform",
                            isActive ? "translate-x-0.5 text-white" : "text-muted-foreground group-hover:translate-x-0.5"
                          )}
                        />
                      </div>
                    </button>
                  );
                })}
              </nav>
            </Card>

            {/* Quick System Health Box */}
            <Card className="border shadow-xs bg-card/50 backdrop-blur-md rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                <Sparkles className="h-4 w-4 text-primary" />
                <span>Ayurdnyanam Platform</span>
              </div>
              <div className="space-y-1.5 text-[11px] text-muted-foreground">
                <div className="flex justify-between">
                  <span>Backend API:</span>
                  <span className="font-mono text-emerald-500 font-bold">Online</span>
                </div>
                <div className="flex justify-between">
                  <span>Max Devices per Student:</span>
                  <span className="font-semibold text-foreground">{formData.maxAllowedDevices}</span>
                </div>
                <div className="flex justify-between">
                  <span>Active Gateway:</span>
                  <span className="font-semibold text-foreground capitalize">{formData.paymentGateway}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* RIGHT COLUMN: Active Setting Category Form */}
          <div className="lg:col-span-8">
            {/* 1. GENERAL & BRANDING */}
            {activeTab === "general" && (
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden animate-in fade-in-50 duration-300">
                <CardHeader className="border-b bg-muted/20 pb-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">General & Platform Branding</CardTitle>
                      <CardDescription className="text-xs">Customize the title, logos, and identity shown across the web & apps.</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold">Application Name</Label>
                      <Input
                        className="h-11 rounded-xl"
                        placeholder="e.g. Ayurdnyanam"
                        value={formData.appName}
                        onChange={(e) => handleChange("appName", e.target.value)}
                      />
                      <p className="text-[11px] text-muted-foreground">Displayed in the browser title bar, student emails, and headers.</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-bold">Brand Tagline</Label>
                      <Input
                        className="h-11 rounded-xl"
                        placeholder="e.g. The Premier Ayurvedic Learning Platform"
                        value={formData.appTagline}
                        onChange={(e) => handleChange("appTagline", e.target.value)}
                      />
                      <p className="text-[11px] text-muted-foreground">Catchphrase shown on student landing pages and invoices.</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold">App Logo URL</Label>
                    <div className="flex gap-3">
                      <Input
                        className="h-11 rounded-xl font-mono text-xs flex-1"
                        placeholder="https://example.com/logo.png"
                        value={formData.appLogo}
                        onChange={(e) => handleChange("appLogo", e.target.value)}
                      />
                      {formData.appLogo && (
                        <div className="h-11 w-11 rounded-xl border bg-muted/40 flex items-center justify-center overflow-hidden shrink-0">
                          <img src={formData.appLogo} alt="Logo preview" className="h-full w-full object-contain p-1" />
                        </div>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Direct URL of your primary logo image (PNG/SVG with transparent background).</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 2. DYNAMIC RAZORPAY & PAYMENTS */}
            {activeTab === "razorpay" && (
              <div className="space-y-6 animate-in fade-in-50 duration-300">
                <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden">
                  <CardHeader className="border-b bg-muted/20 pb-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                          <CreditCard className="h-5 w-5" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Dynamic Razorpay Gateway Configuration</CardTitle>
                          <CardDescription className="text-xs">Configure Razorpay API credentials for student checkouts and course payments.</CardDescription>
                        </div>
                      </div>
                      <Badge className={cn("uppercase text-[10px] font-bold", formData.paymentMode === "live" ? "bg-emerald-600 text-white" : "bg-amber-600 text-white")}>
                        {formData.paymentMode === "live" ? "Live Production" : "Test Sandbox"}
                      </Badge>
                    </div>
                  </CardHeader>

                  <CardContent className="p-6 space-y-6">
                    {/* Payment Mode Switcher Banner */}
                    <div className={cn("p-4 rounded-2xl border flex items-center justify-between", formData.paymentMode === "live" ? "bg-emerald-500/5 border-emerald-500/20" : "bg-amber-500/5 border-amber-500/20")}>
                      <div className="flex items-center gap-3">
                        <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center font-bold", formData.paymentMode === "live" ? "bg-emerald-500 text-white" : "bg-amber-500 text-white")}>
                          {formData.paymentMode === "live" ? "₹" : "T"}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-foreground">
                            {formData.paymentMode === "live" ? "Live Payment Processing" : "Test Sandbox Mode"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formData.paymentMode === "live"
                              ? "Real transactions will be charged via Razorpay Live."
                              : "Dummy test payments using test cards will be simulated."}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="mode-switch" className="text-xs font-bold cursor-pointer">
                          {formData.paymentMode === "live" ? "Live Mode" : "Test Mode"}
                        </Label>
                        <Switch
                          id="mode-switch"
                          checked={formData.paymentMode === "live"}
                          onCheckedChange={(checked) => handleChange("paymentMode", checked ? "live" : "test")}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Razorpay Key ID */}
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5">
                          <Key className="h-3.5 w-3.5 text-primary" /> Razorpay Key ID
                        </Label>
                        <div className="relative">
                          <Input
                            className="h-11 rounded-xl font-mono text-xs pr-10"
                            placeholder={formData.paymentMode === "live" ? "rzp_live_..." : "rzp_test_..."}
                            value={formData.paymentApiKey}
                            onChange={(e) => handleChange("paymentApiKey", e.target.value)}
                          />
                          {formData.paymentApiKey && (
                            <button
                              type="button"
                              onClick={() => handleCopy(formData.paymentApiKey, "apiKey")}
                              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded-lg"
                            >
                              {copiedField === "apiKey" ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                            </button>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">Found in Razorpay Dashboard &gt; Settings &gt; API Keys.</p>
                      </div>

                      {/* Razorpay Key Secret */}
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5">
                          <Lock className="h-3.5 w-3.5 text-primary" /> Razorpay Key Secret
                        </Label>
                        <div className="relative">
                          <Input
                            type={showKeySecret ? "text" : "password"}
                            className="h-11 rounded-xl font-mono text-xs pr-10"
                            placeholder="Enter Razorpay Key Secret"
                            value={formData.paymentApiSecret}
                            onChange={(e) => handleChange("paymentApiSecret", e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setShowKeySecret(!showKeySecret)}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded-lg"
                          >
                            {showKeySecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Keep confidential. Used for verifying payment signatures on the backend.</p>
                      </div>
                    </div>

                    {/* Razorpay Webhook Secret */}
                    <div className="space-y-2 pt-2 border-t border-border/40">
                      <Label className="text-xs font-bold flex items-center gap-1.5">
                        <ShieldAlert className="h-3.5 w-3.5 text-emerald-500" /> Razorpay Webhook Secret
                      </Label>
                      <div className="relative">
                        <Input
                          type={showWebhookSecret ? "text" : "password"}
                          className="h-11 rounded-xl font-mono text-xs pr-10"
                          placeholder="e.g. your_webhook_secret_key"
                          value={formData.paymentWebhookSecret}
                          onChange={(e) => handleChange("paymentWebhookSecret", e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded-lg"
                        >
                          {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Optional secret for verifying incoming webhook notifications.</p>
                    </div>

                    {/* Webhook Endpoint Helper */}
                    <div className="p-4 rounded-xl bg-muted/40 border space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-bold text-foreground flex items-center gap-1.5">
                          <Radio className="h-3.5 w-3.5 text-primary" /> Your Webhook Endpoint URL:
                        </Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopy("https://backend.ayurdnyanam.com/api/payment/webhook", "webhookUrl")}
                          className="h-7 text-xs font-bold gap-1"
                        >
                          {copiedField === "webhookUrl" ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                          Copy URL
                        </Button>
                      </div>
                      <div className="p-2.5 bg-background rounded-lg border font-mono text-xs text-muted-foreground select-all">
                        https://backend.ayurdnyanam.com/api/payment/webhook
                      </div>
                      <p className="text-[11px] text-muted-foreground">Subscribe to <code>payment.captured</code>, <code>order.paid</code>, and <code>payment.failed</code> events in Razorpay.</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* 3. SMS & OTP CONFIGURATIONS */}
            {activeTab === "sms" && (
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden animate-in fade-in-50 duration-300">
                <CardHeader className="border-b bg-muted/20 pb-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-500">
                        <MessageSquare className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">SMS Gateway & OTP Authentication</CardTitle>
                        <CardDescription className="text-xs">Configure SMS provider API keys and TRAI DLT approved template headers.</CardDescription>
                      </div>
                    </div>
                    <Badge variant={formData.smsOtpEnabled ? "default" : "secondary"} className="uppercase text-[10px] font-bold">
                      {formData.smsOtpEnabled ? "OTP Active" : "Disabled"}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="p-6 space-y-6">
                  {/* Enable OTP Toggle */}
                  <div className="flex items-center justify-between p-4 rounded-2xl bg-purple-500/5 border border-purple-500/20">
                    <div className="space-y-0.5">
                      <p className="text-sm font-bold text-foreground">Enable Student SMS OTP Verification</p>
                      <p className="text-xs text-muted-foreground">Send 6-digit OTP to student mobile numbers during registration and login.</p>
                    </div>
                    <Switch
                      checked={formData.smsOtpEnabled}
                      onCheckedChange={(checked) => handleChange("smsOtpEnabled", checked)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* SMS Provider */}
                    <div className="space-y-2">
                      <Label className="text-xs font-bold">SMS Service Provider</Label>
                      <Select
                        value={formData.smsProvider}
                        onValueChange={(v) => handleChange("smsProvider", v)}
                      >
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="fast2sms">Fast2SMS (Recommended for India)</SelectItem>
                          <SelectItem value="msg91">MSG91</SelectItem>
                          <SelectItem value="twilio">Twilio</SelectItem>
                          <SelectItem value="twofactor">2Factor.in</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">Select your transactional/OTP SMS route provider.</p>
                    </div>

                    {/* SMS API Key / Auth Token */}
                    <div className="space-y-2">
                      <Label className="text-xs font-bold flex items-center gap-1.5">
                        <Key className="h-3.5 w-3.5 text-purple-500" /> SMS Provider API Key
                      </Label>
                      <div className="relative">
                        <Input
                          type={showSmsSecret ? "text" : "password"}
                          className="h-11 rounded-xl font-mono text-xs pr-10"
                          placeholder="Enter Provider API Key / Auth Token"
                          value={formData.smsApiKey}
                          onChange={(e) => handleChange("smsApiKey", e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSmsSecret(!showSmsSecret)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground rounded-lg"
                        >
                          {showSmsSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-[11px] text-muted-foreground">API Authorization token provided by your SMS portal.</p>
                    </div>
                  </div>

                  {/* DLT Compliance Fields */}
                  <div className="pt-4 border-t border-border/40 space-y-4">
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-muted-foreground">Indian DLT / TRAI Compliance (Optional / Required for DLT routes)</h4>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold">DLT Sender ID / Header</Label>
                        <Input
                          className="h-11 rounded-xl font-mono uppercase text-xs"
                          placeholder="e.g. AYURDN"
                          maxLength={6}
                          value={formData.smsSenderId}
                          onChange={(e) => handleChange("smsSenderId", e.target.value)}
                        />
                        <p className="text-[10px] text-muted-foreground">6-character registered DLT Sender ID.</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold">DLT Entity / Principal ID</Label>
                        <Input
                          className="h-11 rounded-xl font-mono text-xs"
                          placeholder="e.g. 170115..."
                          value={formData.smsEntityId}
                          onChange={(e) => handleChange("smsEntityId", e.target.value)}
                        />
                        <p className="text-[10px] text-muted-foreground">Your 19-digit DLT Registration ID.</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold">DLT Template ID</Label>
                        <Input
                          className="h-11 rounded-xl font-mono text-xs"
                          placeholder="e.g. 170716..."
                          value={formData.smsTemplateId}
                          onChange={(e) => handleChange("smsTemplateId", e.target.value)}
                        />
                        <p className="text-[10px] text-muted-foreground">Template ID approved for OTP SMS.</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 4. SECURITY & DEVICES */}
            {activeTab === "security" && (
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden animate-in fade-in-50 duration-300">
                <CardHeader className="border-b bg-muted/20 pb-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-orange-500/10 flex items-center justify-center text-orange-500">
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Security & Device Access Control</CardTitle>
                      <CardDescription className="text-xs">Prevent course sharing and enforce student account security limits.</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4 p-5 rounded-2xl bg-muted/30 border">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-500">
                          <Users className="h-5 w-5" />
                        </div>
                        <div>
                          <Label className="text-sm font-bold">Max Allowed Devices per Student</Label>
                          <p className="text-[11px] text-muted-foreground">Limit the number of concurrent devices permitted.</p>
                        </div>
                      </div>
                      <Select
                        value={formData.maxAllowedDevices.toString()}
                        onValueChange={(v) => handleChange("maxAllowedDevices", parseInt(v))}
                      >
                        <SelectTrigger className="h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl">
                          <SelectItem value="1">1 Device (Strict - Recommended)</SelectItem>
                          <SelectItem value="2">2 Devices (e.g. Phone + Laptop)</SelectItem>
                          <SelectItem value="3">3 Devices</SelectItem>
                          <SelectItem value="999">Unlimited Devices</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-4 p-5 rounded-2xl bg-muted/30 border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-500">
                            <Smartphone className="h-5 w-5" />
                          </div>
                          <div>
                            <Label className="text-sm font-bold">Block Simultaneous Login</Label>
                            <p className="text-[11px] text-muted-foreground">Auto-logout older sessions when a new login occurs.</p>
                          </div>
                        </div>
                        <Switch
                          checked={formData.blockSimultaneousLogin}
                          onCheckedChange={(v) => handleChange("blockSimultaneousLogin", v)}
                        />
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        When enabled, logging into a new device will immediately terminate existing active student tokens.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 5. PRICING & CHARGES */}
            {activeTab === "pricing" && (
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden animate-in fade-in-50 duration-300">
                <CardHeader className="border-b bg-muted/20 pb-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500">
                      <Percent className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Pricing, Taxes & Fees</CardTitle>
                      <CardDescription className="text-xs">Configure platform transaction fees, GST tax percentage, and base item rates.</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2 p-4 rounded-xl bg-muted/30 border">
                      <Label className="text-xs font-bold flex items-center justify-between">
                        <span>Platform Handling Charge</span>
                        <span className="text-emerald-500 font-mono font-bold">{formData.platformCharge}%</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type="number"
                          className="h-11 rounded-xl pr-8 font-bold"
                          value={formData.platformCharge}
                          onChange={(e) => handleChange("platformCharge", parseFloat(e.target.value) || 0)}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">%</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Added to invoices as platform processing fee.</p>
                    </div>

                    <div className="space-y-2 p-4 rounded-xl bg-muted/30 border">
                      <Label className="text-xs font-bold flex items-center justify-between">
                        <span>Standard GST / Tax Rate</span>
                        <span className="text-primary font-mono font-bold">{formData.taxRate}%</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type="number"
                          className="h-11 rounded-xl pr-8 font-bold"
                          value={formData.taxRate}
                          onChange={(e) => handleChange("taxRate", parseFloat(e.target.value) || 0)}
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-muted-foreground">%</span>
                      </div>
                      <p className="text-[11px] text-muted-foreground">Goods and Services Tax added at checkout.</p>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border/40">
                    <h4 className="text-xs font-black uppercase tracking-wider text-muted-foreground mb-4">Base Default Pricing (INR ₹)</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold">Notes Base Rate</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            className="h-10 rounded-xl pl-7"
                            value={formData.notesPrice}
                            onChange={(e) => handleChange("notesPrice", parseFloat(e.target.value) || 0)}
                          />
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">₹</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold">Videos Base Rate</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            className="h-10 rounded-xl pl-7"
                            value={formData.videosPrice}
                            onChange={(e) => handleChange("videosPrice", parseFloat(e.target.value) || 0)}
                          />
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">₹</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold">Exams Base Rate</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            className="h-10 rounded-xl pl-7"
                            value={formData.examsPrice}
                            onChange={(e) => handleChange("examsPrice", parseFloat(e.target.value) || 0)}
                          />
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">₹</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-bold">Chapter Base Rate</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            className="h-10 rounded-xl pl-7"
                            value={formData.chapterPrice}
                            onChange={(e) => handleChange("chapterPrice", parseFloat(e.target.value) || 0)}
                          />
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">₹</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 6. CONTACT & SUPPORT */}
            {activeTab === "contact" && (
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden animate-in fade-in-50 duration-300">
                <CardHeader className="border-b bg-muted/20 pb-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Contact Channels & Social Media</CardTitle>
                      <CardDescription className="text-xs">Support channels shown on student apps, receipts, and footer links.</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-wider text-muted-foreground mb-3">Direct Support Channels</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-blue-500" /> Support Email</Label>
                        <Input
                          className="h-11 rounded-xl"
                          placeholder="support@ayurdnyanam.com"
                          value={formData.supportEmail}
                          onChange={(e) => handleChange("supportEmail", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-emerald-500" /> Support Phone</Label>
                        <Input
                          className="h-11 rounded-xl"
                          placeholder="+91 9876543210"
                          value={formData.supportPhone}
                          onChange={(e) => handleChange("supportPhone", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5"><MessageCircle className="h-3.5 w-3.5 text-emerald-600" /> WhatsApp Number</Label>
                        <Input
                          className="h-11 rounded-xl"
                          placeholder="+91 9876543210"
                          value={formData.supportWhatsApp}
                          onChange={(e) => handleChange("supportWhatsApp", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border/40">
                    <h4 className="text-xs font-black uppercase tracking-wider text-muted-foreground mb-3">Official Social Media URLs</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5"><Youtube className="h-3.5 w-3.5 text-red-500" /> YouTube Channel</Label>
                        <Input
                          className="h-11 rounded-xl text-xs"
                          placeholder="https://youtube.com/@ayurdnyanam"
                          value={formData.socialYoutube}
                          onChange={(e) => handleChange("socialYoutube", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5"><Instagram className="h-3.5 w-3.5 text-pink-500" /> Instagram Handle</Label>
                        <Input
                          className="h-11 rounded-xl text-xs"
                          placeholder="https://instagram.com/ayurdnyanam"
                          value={formData.socialInstagram}
                          onChange={(e) => handleChange("socialInstagram", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold flex items-center gap-1.5"><Telegram className="h-3.5 w-3.5 text-blue-400" /> Telegram Group</Label>
                        <Input
                          className="h-11 rounded-xl text-xs"
                          placeholder="https://t.me/ayurdnyanam"
                          value={formData.socialTelegram}
                          onChange={(e) => handleChange("socialTelegram", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border/40">
                    <h4 className="text-xs font-black uppercase tracking-wider text-muted-foreground mb-3">Institute Address & Tax ID</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-bold">Physical Address</Label>
                        <Textarea
                          className="min-h-[90px] rounded-xl text-xs"
                          placeholder="Registered institute location..."
                          value={formData.instituteAddress}
                          onChange={(e) => handleChange("instituteAddress", e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-bold">GSTIN Number</Label>
                        <Input
                          className="h-11 rounded-xl font-mono uppercase text-xs"
                          placeholder="e.g. 27AAAAA0000A1Z5"
                          value={formData.gstin}
                          onChange={(e) => handleChange("gstin", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 7. LEGAL & POLICIES */}
            {activeTab === "legal" && (
              <Card className="border shadow-sm bg-card/90 backdrop-blur-md rounded-2xl overflow-hidden animate-in fade-in-50 duration-300">
                <CardHeader className="border-b bg-muted/20 pb-5">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Terms, Privacy & Refund Policies</CardTitle>
                      <CardDescription className="text-xs">Manage legal policies presented to students during checkout and registration.</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold">Terms & Conditions</Label>
                    <Textarea
                      className="min-h-[160px] rounded-xl text-xs font-mono"
                      placeholder="Enter the official platform terms of service..."
                      value={formData.termsAndConditions}
                      onChange={(e) => handleChange("termsAndConditions", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold">Payment & Refund Policy</Label>
                    <Textarea
                      className="min-h-[160px] rounded-xl text-xs font-mono"
                      placeholder="Enter payment, cancellation, and refund terms..."
                      value={formData.paymentPolicy}
                      onChange={(e) => handleChange("paymentPolicy", e.target.value)}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}